import type { TFunction } from 'i18next'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { generateTitle } from 'features/cart/cartItemTitle'
import { getBookPhotosUids } from 'services/PhotoBook/photoBookUtils'
import { type AbandonedCartPreviewItem } from '@mixtiles/web-backend-shared'
import { type TileData } from 'stores/tilesStore/tiles/tileSchemas'
import { type RemotePhoto } from 'components/PhotoBank/PhotoBank.types'
import {
  MIXED_MATERIALS,
  MIXED_SIZES,
} from 'pages/PhotoStyler/TileDesignerConsts'
import { selectPreviewTileSpecs } from './selectPreviewTileSpecs'
import { getStoredCoverPreviewUrl } from '../coverPreviewUrl'
import { type AbandonedCartItem } from './abandonedCart.schemas'
import { type AbandonedCartSnapshot } from './abandonedCart.types'

function deriveBulkOptions(tiles: TileData[]): {
  tileSize: string
  materialType: string
} {
  const sizes = uniq(tiles.map((tile) => tile.tileSize))
  const materials = uniq(tiles.map((tile) => tile.materialType))
  return {
    tileSize: sizes.length === 1 ? sizes[0] : MIXED_SIZES,
    materialType: materials.length === 1 ? materials[0] : MIXED_MATERIALS,
  }
}

function indexRemotePhotosByUid(
  remotePhotos: RemotePhoto[]
): Record<string, RemotePhoto> {
  return Object.fromEntries(
    remotePhotos
      .filter((photo): photo is RemotePhoto => !!photo?.uid)
      .map((photo) => [photo.uid, photo])
  )
}

export function buildAbandonedCart(
  { cartItems, tiles, books, remotePhotos }: AbandonedCartSnapshot,
  t: TFunction,
  pricingCountry: string,
  isPriceTierListEnabled: boolean
): { items: AbandonedCartItem[]; previewItems: AbandonedCartPreviewItem[] } {
  const remotePhotoByUid = indexRemotePhotosByUid(remotePhotos)
  const tileDatas = tiles.map(({ tileData }) => tileData)
  const bulkOptions = deriveBulkOptions(tileDatas)
  const items: AbandonedCartItem[] = []
  const previewItems: AbandonedCartPreviewItem[] = []

  for (const cartItem of cartItems) {
    const title = generateTitle(
      cartItem,
      t,
      pricingCountry,
      isPriceTierListEnabled
    )

    if (cartItem.productType === PRODUCT_TYPES.CLASSIC) {
      const restorableTiles = tiles.filter(({ photoUids }) =>
        photoUids.every((uid) => !!remotePhotoByUid[uid])
      )
      items.push({
        productType: cartItem.productType,
        title,
        restorePayload: {
          tiles: restorableTiles.map(({ tileData }) => tileData),
          photos: uniqBy(
            restorableTiles
              .flatMap(({ photoUids }) => photoUids)
              .map((uid) => remotePhotoByUid[uid]),
            'uid'
          ),
          bulkOptions,
        },
      })
      const previewTiles = selectPreviewTileSpecs({
        tiles: tileDatas,
        remotePhotoByUid,
      })
      if (previewTiles.length > 0) {
        previewItems.push({
          productType: cartItem.productType,
          tiles: previewTiles,
        })
      }
      continue
    }

    if (cartItem.productType === PRODUCT_TYPES.PHOTO_BOOK) {
      const book = books.find((candidate) => candidate.bookId === cartItem.id)
      if (!book) continue
      const bookPhotoUids = new Set(getBookPhotosUids(book.cover, book.pages))
      items.push({
        productType: cartItem.productType,
        title,
        restorePayload: {
          book,
          photos: remotePhotos.filter((photo) => bookPhotoUids.has(photo.uid)),
        },
      })
      const coverUrl = getStoredCoverPreviewUrl(book.cover, remotePhotoByUid)
      if (coverUrl) {
        previewItems.push({
          productType: cartItem.productType,
          coverUrl,
          coverCaption: book.cover.caption?.text,
        })
      }
      continue
    }
  }

  return { items, previewItems }
}

export function buildBrazeAttributes(
  items: AbandonedCartItem[]
): Record<string, unknown> {
  return {
    'Product Types': uniq(items.map((item) => item.productType)),
    'Item Count': items.length,
    'Item Titles': items.map((item) => item.title),
  }
}
